import {SlackIcon} from '@github-ui/custom-icons/SlackIcon'
import {DragAndDrop} from '@github-ui/drag-and-drop'
import {matchesFocusVisible, useFocusVisibleWithin} from '@github-ui/focus-visible'
import {sendEvent} from '@github-ui/hydro-analytics'
import type {ScribeArtifact} from '@github-ui/scribe-artifacts'
import {getArtifactLinkDisplayName} from '@github-ui/scribe-artifacts/artifact-url'
import {ArtifactLink} from '@github-ui/scribe-artifacts/ArtifactLink'
import type {PanelArtifact} from '@github-ui/scribe-artifacts/ArtifactPanelContent'
import {
  ChecklistIcon,
  FileIcon,
  GrabberIcon,
  KebabHorizontalIcon,
  LinkIcon,
  PencilIcon,
  TrashIcon,
} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton, registerPortalRoot} from '@primer/react'
import {clsx} from 'clsx'
import {memo, type RefObject, useCallback, useEffect, useRef, useState} from 'react'

import styles from './ArtifactRow.module.css'
import type {EditableArtifact} from './create-artifact-dialog-types'
import type {IssueArtifact} from './types'

function ArtifactTypeIconGraphic({type, body}: {type: IssueArtifact['type']; body: string}) {
  const isSlackLink = type === 'link' && isSlackUrl(body)
  const Icon = type === 'plan' ? ChecklistIcon : type === 'link' ? (isSlackLink ? SlackIcon : LinkIcon) : FileIcon

  return isSlackLink ? <SlackIcon /> : <Icon size={14} className={styles.Icon} />
}

function ArtifactTypeIcon({type, body}: {type: IssueArtifact['type']; body: string}) {
  const isSlackLink = type === 'link' && isSlackUrl(body)
  return (
    <span className={styles.IconWrap} data-testid={isSlackLink ? 'slack-icon' : undefined}>
      <ArtifactTypeIconGraphic type={type} body={body} />
    </span>
  )
}

function SortableArtifactTypeIcon({
  title,
  type,
  body,
  disabled,
}: {
  title: string
  type: IssueArtifact['type']
  body: string
  disabled: boolean
}) {
  const isSlackLink = type === 'link' && isSlackUrl(body)

  return (
    <span className={styles.IconWrap} data-testid={isSlackLink ? 'slack-icon' : undefined}>
      <ArtifactTypeIconGraphic type={type} body={body} />
      <span className={styles.DragTriggerOverlay} data-disabled={disabled || undefined}>
        <DragAndDrop.DragTrigger
          className={styles.DragTrigger}
          aria-label={`Move ${title}`}
          aria-disabled={disabled}
          tabIndex={disabled ? -1 : undefined}
          {...(disabled ? {onKeyDown: (event: React.KeyboardEvent) => event.preventDefault()} : {})}
        />
      </span>
    </span>
  )
}

function isSlackUrl(value: string): boolean {
  try {
    const url = new URL(value, 'https://github.com')
    return (
      (url.protocol === 'https:' || url.protocol === 'http:') &&
      (url.hostname === 'slack.com' || url.hostname.endsWith('.slack.com'))
    )
  } catch {
    return false
  }
}

type PersistedArtifactRowProps = {
  id: number
  number: number
  type: IssueArtifact['type']
  name: string
  body: string
  bodyHTML?: string
  trustedBodyHTML?: NonNullable<ScribeArtifact['body_html']>
  createdAt?: string
  updatedAt?: string
  hasEdits?: boolean
  updatedByActor?: IssueArtifact['updatedByActor']
  resourceId: number
  repositoryId?: number
  organizationId?: number
  onDelete?: (id: number) => void
  onEdit?: (artifact: EditableArtifact, returnFocusRef: RefObject<HTMLButtonElement | null>) => void
  onSelect?: (artifact: PanelArtifact) => void
  reorderPending?: boolean
}

function ArtifactRowContent({
  id,
  number,
  type,
  name,
  body,
  bodyHTML,
  trustedBodyHTML,
  createdAt,
  updatedAt,
  hasEdits,
  updatedByActor,
  resourceId,
  repositoryId,
  organizationId,
  onDelete,
  onEdit,
  onSelect,
  reorderPending = false,
  sortable = false,
  isDragOverlay = false,
  menuOpen,
  onMenuOpenChange,
}: PersistedArtifactRowProps & {
  sortable?: boolean
  isDragOverlay?: boolean
  menuOpen: boolean
  onMenuOpenChange: (open: boolean) => void
}) {
  const actionsButtonRef = useRef<HTMLButtonElement>(null)
  const rowRef = useRef<HTMLDivElement>(null)
  const isLink = type === 'link'
  const displayName = isLink ? getArtifactLinkDisplayName(name, body) : name

  // `titleFocusVisible` replaces a `:has()` selector on `.Row` to avoid WebKit style
  // invalidation across the artifact list. The actions reveal uses the shared
  // `useFocusVisibleWithin` helper, which toggles `data-focus-visible-within`
  // on the actions container via delegated listeners (no re-render).
  const [titleFocusVisible, setTitleFocusVisible] = useState(false)
  const actionsFocusVisibleRef = useFocusVisibleWithin()

  // Use a body-level portal to keep the menu outside the embedded panel's stacking context.
  useEffect(() => {
    registerPortalRoot(document.body, 'issue-artifact-action-menu')
  }, [])

  const handleTitleFocus = useCallback((event: React.FocusEvent<HTMLElement>) => {
    setTitleFocusVisible(matchesFocusVisible(event.currentTarget))
  }, [])
  const handleTitleBlur = useCallback(() => setTitleFocusVisible(false), [])
  const recordArtifactOpen = useCallback(() => {
    sendEvent('issue_artifact.open', {
      artifact_id: id,
      artifact_number: number,
      artifact_type: type,
      issue_id: resourceId,
      repository_id: repositoryId,
      organization_id: organizationId,
      platform: 'dotcom',
    })
  }, [id, number, type, resourceId, repositoryId, organizationId])
  const handleAuxClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (event.button === 1) recordArtifactOpen()
    },
    [recordArtifactOpen],
  )
  const handleClick = useCallback(() => {
    recordArtifactOpen()
    onSelect?.({
      id: `${type}:${id}`,
      number,
      type,
      name,
      body,
      bodyHTML: bodyHTML ?? '',
      trustedBodyHTML,
      createdAt,
      updatedAt,
      hasEdits,
      updatedByActor,
      resourceType: 'issue',
      resourceId,
    })
  }, [
    id,
    number,
    type,
    name,
    body,
    bodyHTML,
    trustedBodyHTML,
    createdAt,
    updatedAt,
    hasEdits,
    updatedByActor,
    resourceId,
    onSelect,
    recordArtifactOpen,
  ])

  const handleDelete = useCallback(() => {
    onDelete?.(id)
  }, [id, onDelete])

  const handleEdit = useCallback(() => {
    onEdit?.({number, type, name, body}, actionsButtonRef)
  }, [body, name, number, onEdit, type])

  const setActionMenuOverlayRef = useCallback((node: HTMLDivElement | null) => {
    // Primer's portal wrapper controls the menu's stacking level.
    if (node?.parentElement) node.parentElement.style.zIndex = 'var(--zIndex-overlay)'
  }, [])

  return (
    <div ref={rowRef} className={clsx(styles.RowSurface, titleFocusVisible && styles.RowTitleFocusVisible)}>
      {sortable ? (
        <SortableArtifactTypeIcon title={displayName} type={type} body={body} disabled={reorderPending} />
      ) : (
        <ArtifactTypeIcon type={type} body={body} />
      )}
      <div className={styles.Content}>
        {isLink ? (
          <ArtifactLink
            name={name}
            url={body}
            updatedAt={updatedAt}
            updatedByActor={updatedByActor}
            className={styles.TitleButton}
            textClassName={styles.Title}
            popoverAnchorRef={rowRef}
            onClick={recordArtifactOpen}
            onAuxClick={handleAuxClick}
            onFocus={handleTitleFocus}
            onBlur={handleTitleBlur}
            renderFallback={fallbackDisplayName => (
              <button
                type="button"
                className={styles.TitleButton}
                aria-label={fallbackDisplayName}
                onClick={handleClick}
                onFocus={handleTitleFocus}
                onBlur={handleTitleBlur}
              >
                <span className={styles.Title}>{fallbackDisplayName}</span>
              </button>
            )}
          />
        ) : (
          <button
            type="button"
            className={styles.TitleButton}
            aria-label={displayName}
            onClick={handleClick}
            onFocus={handleTitleFocus}
            onBlur={handleTitleBlur}
          >
            <span className={styles.Title}>{displayName}</span>
          </button>
        )}
      </div>
      {!isDragOverlay && (
        <div className={styles.HoverActions} ref={actionsFocusVisibleRef}>
          <ActionMenu open={menuOpen} onOpenChange={onMenuOpenChange}>
            <ActionMenu.Anchor>
              <IconButton
                ref={actionsButtonRef}
                icon={KebabHorizontalIcon}
                variant="invisible"
                size="small"
                aria-label={`More actions for ${displayName}`}
              />
            </ActionMenu.Anchor>
            <ActionMenu.Overlay ref={setActionMenuOverlayRef} portalContainerName="issue-artifact-action-menu">
              <ActionList>
                <ActionList.Item onSelect={handleEdit}>
                  <ActionList.LeadingVisual>
                    <PencilIcon />
                  </ActionList.LeadingVisual>
                  Edit
                </ActionList.Item>
                <ActionList.Item variant="danger" onSelect={handleDelete}>
                  <ActionList.LeadingVisual>
                    <TrashIcon />
                  </ActionList.LeadingVisual>
                  Delete
                </ActionList.Item>
                {sortable && (
                  <DragAndDrop.MoveDialogTrigger
                    Component={ActionListItemReorder}
                    returnFocusRef={actionsButtonRef}
                    disabled={reorderPending}
                  />
                )}
              </ActionList>
            </ActionMenu.Overlay>
          </ActionMenu>
        </div>
      )}
    </div>
  )
}

export const PersistedArtifactRow = memo(function PersistedArtifactRowImpl(props: PersistedArtifactRowProps) {
  const [menuOpen, setMenuOpen] = useState(false)

  return (
    <li className={clsx(styles.Row, menuOpen && styles.RowMenuOpen)}>
      <ArtifactRowContent {...props} menuOpen={menuOpen} onMenuOpenChange={setMenuOpen} />
    </li>
  )
})

type SortablePersistedArtifactRowProps = PersistedArtifactRowProps & {
  index: number
  isDragOverlay?: boolean
}

export const SortablePersistedArtifactRow = memo(function SortablePersistedArtifactRowImpl({
  index,
  isDragOverlay = false,
  ...props
}: SortablePersistedArtifactRowProps) {
  const [menuOpen, setMenuOpen] = useState(false)

  return (
    <DragAndDrop.Item
      aria-label={props.name}
      className={clsx(styles.Row, menuOpen && styles.RowMenuOpen)}
      hideTrigger
      id={props.number}
      index={index}
      isDragOverlay={isDragOverlay}
      title={props.name}
    >
      <ArtifactRowContent
        {...props}
        sortable
        isDragOverlay={isDragOverlay}
        menuOpen={menuOpen}
        onMenuOpenChange={setMenuOpen}
      />
    </DragAndDrop.Item>
  )
})

function ActionListItemReorder({
  onClick,
  onSelect,
  disabled,
}: {
  onClick?: () => void
  onSelect?: () => void
  disabled?: boolean
}) {
  return (
    <ActionList.Item onSelect={disabled ? undefined : (onSelect ?? onClick)} disabled={disabled}>
      <ActionList.LeadingVisual>
        <GrabberIcon />
      </ActionList.LeadingVisual>
      Move
    </ActionList.Item>
  )
}
